import { useReactiveVar } from '@apollo/client'
import classNames from 'classnames'
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Autoplay, FreeMode, Navigation, Pagination, Thumbs } from 'swiper'
import {
  Swiper,
  SwiperClass,
  SwiperProps,
  SwiperRef,
  SwiperSlide
} from 'swiper/react'
import 'swiper/swiper-bundle.min.css'
import { SwiperEvents, SwiperModule } from 'swiper/types'

import BREAKPOINTS from '@/constants/Breakpoints'

import { SliderHookVar } from '@/hooks/useSlider/useSlider'

import Image from '@/components/shared/elements/Image/Image'

import STYLES from './SwiperSlider.module.sass'

type Props = Pick<
  SwiperProps,
  'onActiveIndexChange' | 'watchOverflow' | 'spaceBetween' | 'allowTouchMove'
> & {
  children: JSX.Element | null | Array<JSX.Element | null>
  slidesPerView?: number | 'auto'
  bullets?: boolean
  arrows?: boolean
  thumbs?: boolean
  autoPlay?: boolean
  arrowsInside?: boolean
  arrowPosition?: number // allows vertical adjustment of the prev/next buttons
  initialSlide?: number
  className?: string
  slider?: SwiperProps['onSwiper']
  autoHeight?: boolean // Set to `true` and slider wrapper will adapt its height to the height of the currently active slide
  paddingOverride?: 0 | 8 | 16 | 24 | 32
  id?: string
  loop?: boolean
  centeredSlides?: boolean
  onArrowClick?: () => void
  onLazyLoadNext?: () => void
  onLazyLoadPrev?: () => void
  onSlideChange?: SwiperEvents['slideChange']
  onSliderFirstMove?: SwiperEvents['sliderFirstMove']
  onThumbnailClick?: SwiperEvents['click']
  previousButtonAriaLabel?: string
  nextButtonAriaLabel?: string
  isCentered?: boolean
}

const SwiperSlider = ({
  children,
  slidesPerView = 'auto',
  bullets = false,
  arrows,
  thumbs = false,
  autoPlay = false,
  arrowsInside = false,
  arrowPosition,
  spaceBetween = 16,
  initialSlide = 0,
  slider,
  watchOverflow,
  className,
  autoHeight = false,
  paddingOverride = 0,
  id,
  allowTouchMove = true,
  loop = false,
  centeredSlides = false,
  onArrowClick,
  onLazyLoadNext,
  onLazyLoadPrev,
  onSlideChange,
  onSliderFirstMove,
  onThumbnailClick,
  previousButtonAriaLabel,
  nextButtonAriaLabel,
  isCentered = false
}: Props): JSX.Element | null => {
  const sliderRef = useRef<SwiperRef | null>(null)
  const prevButton = useRef<HTMLDivElement>(null)
  const nextButton = useRef<HTMLDivElement>(null)
  const sliderIndexUpdate = useReactiveVar(SliderHookVar)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null)
  const [slides, setSlides] = useState<number>(0)
  const [initialised, setInitialised] = useState<boolean>(false)
  const [isLoadingPrev, setIsLoadingPrev] = useState<boolean>(false)
  const [isLoadingNext, setIsLoadingNext] = useState<boolean>(false)
  const childNodes = Array.isArray(children)
    ? children
    : children !== null
    ? [children]
    : []

  const isMobile = window.innerWidth < BREAKPOINTS.md
  const hasArrows = useMemo(
    () => arrows || (arrows === undefined && !isMobile),
    [arrows, isMobile]
  )

  useEffect(() => {
    if (sliderIndexUpdate === null || sliderIndexUpdate.id !== id) return
    switch (sliderIndexUpdate.slide) {
      case 'first':
        sliderRef.current?.swiper.slideTo(0)
        break
      case 'last':
        sliderRef.current?.swiper.slideTo(slides - 1)
        break
      case 'next':
        sliderRef.current?.swiper.slideNext()
        break
      case 'prev':
        sliderRef.current?.swiper.slidePrev()
        break
      default:
        sliderRef.current?.swiper.slideTo(sliderIndexUpdate.slide)
        break
    }
    SliderHookVar(null)
  }, [sliderIndexUpdate, id, slides])

  useEffect(() => {
    const swiper = sliderRef.current?.swiper
    if (!swiper) return
    const newSlidesAdded = swiper.slides.length - slides
    if (newSlidesAdded > 0) {
      if (isLoadingPrev && onLazyLoadPrev !== undefined) {
        swiper.slideTo(1)
      }
      setIsLoadingNext(false)
      setIsLoadingPrev(false)
      setSlides(swiper.slides.length)
    }
  }, [
    initialSlide,
    initialised,
    isLoadingNext,
    isLoadingPrev,
    onLazyLoadPrev,
    slides
  ])

  useEffect(() => {
    const swiper = sliderRef.current?.swiper
    if (
      swiper &&
      !initialised &&
      swiper.slides.length > 0 &&
      swiper.slides.length >= slides
    ) {
      swiper.slideTo(initialSlide)
      setInitialised(true)
    }
  }, [initialSlide, initialised, slides])

  const generateModules = (
    arrows: boolean,
    bullets: boolean,
    thumbs: boolean,
    autoPlay: boolean
  ): SwiperModule[] => {
    const modules = []
    if (arrows) modules.push(Navigation)
    if (bullets) modules.push(Pagination)
    if (thumbs) modules.push(Thumbs)
    if (autoPlay) modules.push(Autoplay)
    return modules
  }

  const propModules = generateModules(hasArrows, bullets, thumbs, autoPlay)

  /**
   * Fires on an interaction which should trigger a lazy load of slides
   */
  const lazyLoadEvent = useCallback(
    (swiper: SwiperClass) => {
      if (
        onLazyLoadPrev !== undefined &&
        swiper.isBeginning &&
        swiper.swipeDirection !== 'next'
      ) {
        setIsLoadingPrev(true)
        onLazyLoadPrev()
      }
      if (
        onLazyLoadNext !== undefined &&
        swiper.isEnd &&
        swiper.swipeDirection !== 'prev'
      ) {
        setIsLoadingNext(true)
        onLazyLoadNext()
      }
    },
    [onLazyLoadPrev, onLazyLoadNext]
  )

  // Click handler for the arrow buttons.
  // The navigation object handles the click events, so we don't need an onClick.
  // Adding this one here to take advantage of the IconButton atom,
  // which has an onClick as a required prop.
  const onClick = useCallback(
    (e) => {
      e.preventDefault()

      if (sliderRef.current !== null) lazyLoadEvent(sliderRef.current.swiper)

      onArrowClick?.()
    },
    [lazyLoadEvent, onArrowClick]
  )

  const onSwiper = (swiper: SwiperClass): void => {
    if (slider) slider(swiper)

    // Delay execution for the refs to be defined
    const navigation = swiper.params.navigation
    if (
      typeof navigation === 'boolean' ||
      !hasArrows ||
      navigation === undefined
    )
      return
    setTimeout(() => {
      // Override prevEl & nextEl now that refs are defined
      navigation.prevEl = prevButton.current
      navigation.nextEl = nextButton.current

      if (swiper?.navigation === undefined) return

      // Re-init navigation
      swiper.navigation.destroy()
      swiper.navigation.init()
      swiper.navigation.update()
    })
  }

  const classes = classNames(STYLES.swiperSlider, {
    [STYLES.withThumbs]: thumbs,
    [STYLES.withBullets]: bullets,
    [STYLES.arrowsInside]: hasArrows && arrowsInside,
    [STYLES.withLazyLoadPrev]: onLazyLoadPrev,
    [STYLES.withLazyLoadNext]: onLazyLoadNext,
    [STYLES[`withPaddingOverride${paddingOverride}`]]: paddingOverride !== 0
  })

  const arrowStyles: CSSProperties | undefined =
    arrowPosition !== undefined
      ? {
          // eslint-disable-next-line i18next/no-literal-string
          transform: `translate(0, ${arrowPosition}rem)`
        }
      : undefined

  return (
    <div className={classes} id={id}>
      <div className={STYLES.sliderWrapper}>
        <Swiper
          ref={sliderRef}
          spaceBetween={spaceBetween}
          slidesOffsetBefore={paddingOverride}
          slidesOffsetAfter={paddingOverride}
          slidesPerView={slidesPerView}
          modules={propModules}
          navigation={
            hasArrows
              ? {
                  nextEl: nextButton.current,
                  prevEl: prevButton.current
                }
              : hasArrows
          }
          pagination={bullets ? { clickable: true } : bullets}
          thumbs={{ swiper: thumbs ? thumbsSwiper : null }}
          autoplay={
            autoPlay ? { delay: 2500, disableOnInteraction: false } : autoPlay
          }
          initialSlide={initialSlide}
          className={className}
          observer
          // eslint-disable-next-line react/jsx-no-bind
          onSwiper={onSwiper}
          onSliderMove={lazyLoadEvent}
          watchOverflow={watchOverflow}
          autoHeight={autoHeight}
          effect="fade"
          centeredSlidesBounds
          onSliderFirstMove={onSliderFirstMove}
          allowTouchMove={allowTouchMove}
          centeredSlides={centeredSlides}
          loop={loop}
          onSlideChange={onSlideChange}
        >
          {childNodes.map((child) => {
            if (child === null) return null
            return (
              // eslint-disable-next-line react/no-array-index-key
              <SwiperSlide key={child.key}>
                <div
                  className={`${STYLES.slideContent} ${
                    isCentered ? STYLES.slideCentered : ''
                  }`}
                >
                  {child}
                </div>
              </SwiperSlide>
            )
          })}
        </Swiper>
        {hasArrows && (
          <>
            <div
              className={STYLES.arrowLeft}
              ref={prevButton}
              style={arrowStyles}
            >
              <button
                className={STYLES.button}
                type="button"
                onClick={onClick}
                aria-label={previousButtonAriaLabel}
              >
                <Image
                  slug="chevron-blue-500"
                  alt=""
                  image={{
                    width: 16,
                    height: 19
                  }}
                />
              </button>
            </div>
            <div
              className={STYLES.arrowRight}
              ref={nextButton}
              style={arrowStyles}
            >
              <button
                className={STYLES.button}
                type="button"
                onClick={onClick}
                aria-label={nextButtonAriaLabel}
              >
                <Image
                  slug="chevron-blue-500"
                  alt=""
                  image={{
                    width: 16,
                    height: 19
                  }}
                />
              </button>
            </div>
          </>
        )}
      </div>
      {thumbs && (
        <Swiper
          watchSlidesProgress
          onSwiper={setThumbsSwiper}
          slidesPerView={'auto'}
          spaceBetween={8}
          freeMode
          modules={[FreeMode, Navigation, Thumbs]}
          grabCursor
          onClick={onThumbnailClick}
        >
          {childNodes.map((child) => {
            if (child === null) return null
            return (
              // eslint-disable-next-line react/no-array-index-key
              <SwiperSlide key={child.key}>
                <div className={STYLES.slideThumb}>{child}</div>
              </SwiperSlide>
            )
          })}
        </Swiper>
      )}
    </div>
  )
}

export { Props, SliderHookVar }
export default SwiperSlider
